import { c as _c } from "react/compiler-runtime";
import { IcoArrowTopRight16, IcoBook16 } from '@onefootprint/icons';
import type { SourceCitationEvidence } from '@onefootprint/request-types/dashboard';
import { IconButton, Tooltip } from '@onefootprint/ui';
import * as RadixPopover from '@radix-ui/react-popover';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import type { InvestigationSourceDetail } from '../../../../hooks/use-sources';
import { formatSourceDate, humanizeDocumentIdentifier, splitUrl } from '../../../../utils/source-display';
import { SOURCE_BADGE_CLASS } from '../../source-citation-shared';
import CitedPassages, { hasTextPassages } from '../cited-passages/cited-passages';
import SourceDetail from '../source-detail/source-detail';
type SourceCitationPopoverProps = {
  anchorRect: DOMRect | null;
  label: string;
  source: InvestigationSourceDetail | undefined;
  /** Every resolved passage the chip's run cited, paged through one at a time. */
  evidences: SourceCitationEvidence[];
  onClose: () => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onOpenSources?: () => void;
  onOpenDocument?: (documentIdentifier: string) => void;
};
const SourceCitationPopover = t0 => {
  const $ = _c(96);
  const {
    anchorRect,
    label,
    source,
    evidences,
    onClose,
    onPointerEnter,
    onPointerLeave,
    onOpenSources,
    onOpenDocument
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "sourcesDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t1);
  if (!anchorRect) {
    return null;
  }
  const page = source?.data?.kind === "web_fetch" ? source.data.data : undefined;
  let T0;
  let T1;
  let T2;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t2;
  let t20;
  let t21;
  let t22;
  let t23;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== anchorRect.height || $[2] !== anchorRect.left || $[3] !== anchorRect.top || $[4] !== anchorRect.width || $[5] !== evidences || $[6] !== label || $[7] !== onClose || $[8] !== onOpenSources || $[9] !== onPointerEnter || $[10] !== onPointerLeave || $[11] !== page || $[12] !== source || $[13] !== t) {
    const hasBody = page ? hasTextPassages(evidences) : !!source;
    let t24;
    bb0: {
      if (page) {
        const {
          host,
          path
        } = splitUrl(page.url);
        t24 = {
          name: host,
          path,
          kind: t(_temp, {
            date: formatSourceDate(page.fetchedAt)
          })
        };
        break bb0;
      }
      if (source?.documentIdentifier) {
        t24 = {
          name: humanizeDocumentIdentifier(source.documentIdentifier),
          path: "",
          kind: t(_temp2)
        };
        break bb0;
      }
      t24 = {
        name: source?.title ?? "Source not found",
        path: "",
        kind: undefined
      };
    }
    const heading = t24;
    T2 = RadixPopover.Root;
    t22 = true;
    const t25 = anchorRect.width || 1;
    const t26 = anchorRect.height || 1;
    if ($[39] !== anchorRect.left || $[40] !== anchorRect.top || $[41] !== t25 || $[42] !== t26) {
      t23 = <RadixPopover.Anchor style={{
        position: "fixed",
        top: anchorRect.top,
        left: anchorRect.left,
        width: t25,
        height: t26,
        pointerEvents: "none"
      }} />;
      $[39] = anchorRect.left;
      $[40] = anchorRect.top;
      $[41] = t25;
      $[42] = t26;
      $[43] = t23;
    } else {
      t23 = $[43];
    }
    T1 = RadixPopover.Portal;
    T0 = RadixPopover.Content;
    t9 = "bottom";
    t10 = "start";
    t11 = 7;
    t12 = 12;
    t13 = true;
    t14 = onClose;
    t15 = onClose;
    t16 = _temp3;
    t17 = _temp4;
    if ($[44] !== onPointerEnter) {
      t18 = event_1 => {
        if (event_1.pointerType === "mouse") {
          onPointerEnter();
        }
      };
      $[44] = onPointerEnter;
      $[45] = t18;
    } else {
      t18 = $[45];
    }
    if ($[46] !== onPointerLeave) {
      t19 = event_2 => {
        if (event_2.pointerType === "mouse") {
          onPointerLeave();
        }
      };
      $[46] = onPointerLeave;
      $[47] = t19;
    } else {
      t19 = $[47];
    }
    t20 = true;
    t21 = "z-popover w-[520px] max-w-[calc(100vw-32px)] overflow-hidden rounded-default border border-tertiary bg-primary shadow-md outline-none data-[state=open]:animate-scaleIn max-md:max-h-(--radix-popover-content-available-height) max-md:overflow-y-auto";
    t6 = cx("flex items-center gap-3 px-3 py-2", hasBody && "border-b border-tertiary");
    if ($[48] !== label) {
      t7 = <span className={`${SOURCE_BADGE_CLASS} shrink-0 text-accent`}>{label}</span>;
      $[48] = label;
      $[49] = t7;
    } else {
      t7 = $[49];
    }
    t8 = <span className="min-w-0 flex-1"><span className="block truncate text-label-3 text-primary">{heading.name}{heading.path && <span className="text-tertiary">{heading.path}</span>}</span>{heading.kind && <span className="block truncate text-body-3 text-tertiary">{heading.kind}</span>}</span>;
    t2 = "flex shrink-0 items-center gap-2";
    if ($[50] !== onOpenSources || $[51] !== t) {
      t3 = onOpenSources && <Tooltip content={t(_temp5)}><IconButton aria-label={t(_temp6)} icon={IcoBook16} className="max-md:size-9" size="tiny" variant="ghost" onClick={onOpenSources} /></Tooltip>;
      $[50] = onOpenSources;
      $[51] = t;
      $[52] = t3;
    } else {
      t3 = $[52];
    }
    if ($[53] !== onOpenSources || $[54] !== page) {
      t4 = onOpenSources && page && <span aria-hidden={true} className="h-4 w-px bg-(--border-color-tertiary)" />;
      $[53] = onOpenSources;
      $[54] = page;
      $[55] = t4;
    } else {
      t4 = $[55];
    }
    t5 = page && <Tooltip content={t(_temp7)}><IconButton aria-label={t(_temp8, {
        name: heading.name
      })} icon={IcoArrowTopRight16} className="max-md:size-9" size="tiny" variant="ghost" onClick={() => window.open(page.url, "_blank", "noopener,noreferrer")} /></Tooltip>;
    $[1] = anchorRect.height;
    $[2] = anchorRect.left;
    $[3] = anchorRect.top;
    $[4] = anchorRect.width;
    $[5] = evidences;
    $[6] = label;
    $[7] = onClose;
    $[8] = onOpenSources;
    $[9] = onPointerEnter;
    $[10] = onPointerLeave;
    $[11] = page;
    $[12] = source;
    $[13] = t;
    $[14] = T0;
    $[15] = T1;
    $[16] = T2;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
    $[22] = t15;
    $[23] = t16;
    $[24] = t17;
    $[25] = t18;
    $[26] = t19;
    $[27] = t2;
    $[28] = t20;
    $[29] = t21;
    $[30] = t22;
    $[31] = t23;
    $[32] = t3;
    $[33] = t4;
    $[34] = t5;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
  } else {
    T0 = $[14];
    T1 = $[15];
    T2 = $[16];
    t10 = $[17];
    t11 = $[18];
    t12 = $[19];
    t13 = $[20];
    t14 = $[21];
    t15 = $[22];
    t16 = $[23];
    t17 = $[24];
    t18 = $[25];
    t19 = $[26];
    t2 = $[27];
    t20 = $[28];
    t21 = $[29];
    t22 = $[30];
    t23 = $[31];
    t3 = $[32];
    t4 = $[33];
    t5 = $[34];
    t6 = $[35];
    t7 = $[36];
    t8 = $[37];
    t9 = $[38];
  }
  let t24;
  if ($[56] !== t2 || $[57] !== t3 || $[58] !== t4 || $[59] !== t5) {
    t24 = <span className={t2}>{t3}{t4}{t5}</span>;
    $[56] = t2;
    $[57] = t3;
    $[58] = t4;
    $[59] = t5;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== t24 || $[62] !== t6 || $[63] !== t7 || $[64] !== t8) {
    t25 = <div className={t6}>{t7}{t8}{t24}</div>;
    $[61] = t24;
    $[62] = t6;
    $[63] = t7;
    $[64] = t8;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] !== evidences || $[67] !== onOpenDocument || $[68] !== page || $[69] !== source) {
    t26 = page ? <CitedPassages evidences={evidences} paragraphs={page.paragraphs} /> : source ? <SourceDetail data={source.data} documentIdentifier={source.documentIdentifier} evidences={evidences} onOpenDocument={onOpenDocument} /> : null;
    $[66] = evidences;
    $[67] = onOpenDocument;
    $[68] = page;
    $[69] = source;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== T0 || $[72] !== t10 || $[73] !== t11 || $[74] !== t12 || $[75] !== t13 || $[76] !== t14 || $[77] !== t15 || $[78] !== t16 || $[79] !== t17 || $[80] !== t18 || $[81] !== t19 || $[82] !== t20 || $[83] !== t21 || $[84] !== t25 || $[85] !== t26 || $[86] !== t9) {
    t27 = <T0 side={t9} align={t10} sideOffset={t11} collisionPadding={t12} avoidCollisions={t13} onEscapeKeyDown={t14} onPointerDownOutside={t15} onOpenAutoFocus={t16} onCloseAutoFocus={t17} onPointerEnter={t18} onPointerLeave={t19} data-citation-popover={t20} className={t21}>{t25}{t26}</T0>;
    $[71] = T0;
    $[72] = t10;
    $[73] = t11;
    $[74] = t12;
    $[75] = t13;
    $[76] = t14;
    $[77] = t15;
    $[78] = t16;
    $[79] = t17;
    $[80] = t18;
    $[81] = t19;
    $[82] = t20;
    $[83] = t21;
    $[84] = t25;
    $[85] = t26;
    $[86] = t9;
    $[87] = t27;
  } else {
    t27 = $[87];
  }
  let t28;
  if ($[88] !== T1 || $[89] !== t27) {
    t28 = <T1>{t27}</T1>;
    $[88] = T1;
    $[89] = t27;
    $[90] = t28;
  } else {
    t28 = $[90];
  }
  let t29;
  if ($[91] !== T2 || $[92] !== t22 || $[93] !== t23 || $[94] !== t28) {
    t29 = <T2 open={t22}>{t23}{t28}</T2>;
    $[91] = T2;
    $[92] = t22;
    $[93] = t23;
    $[94] = t28;
    $[95] = t29;
  } else {
    t29 = $[95];
  }
  return t29;
};
export default SourceCitationPopover;
function _temp($) {
  return $.fetched;
}
function _temp2($_0) {
  return $_0.vaultDocument;
}
function _temp3(event) {
  return event.preventDefault();
}
function _temp4(event_0) {
  return event_0.preventDefault();
}
function _temp5($_1) {
  return $_1.allSources;
}
function _temp6($_2) {
  return $_2.allSources;
}
function _temp7($_3) {
  return $_3.openPage;
}
function _temp8($_4) {
  return $_4.opensInNewTab;
}